import { Card, CardContent, CardHeader, CardTitle, Input, ScrollArea, Textarea } from '@ibsys/kit';
import { FC, useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { AttributeGroupResponse, useDeleteAttributeGroup } from '@/entities/attributes';
import { AiInput, ConfirmationButton, EditableTitle, SaveButton } from '@/shared/ui';
import { useAttributeGroupForm } from './useAttributeGroupForm';
import { useNavigate } from 'react-router-dom';
import { AttributesList } from '../AttributesList/AttributesList';
import { useFormGuardActions } from '@/entities/formGuard';

type Props = {
  initial?: AttributeGroupResponse;
  onSuccess?: () => void;
  onUnsavedChange?: (hasUnsaved: boolean) => void;
};

export const AttributeGroupForm: FC<Props> = ({ initial, onSuccess, onUnsavedChange }) => {
  const { t } = useTranslation(['common', 'attributeGroups']);
  const navigate = useNavigate();
  const { resetFormGuard } = useFormGuardActions();

  const {
    form,
    isEdit,
    isLoading,
    onSubmit,
    isDisabled,
    handleTitleSave,
    handleNameBlur,
    isCodeLocked,
    isGeneratingCode,
  } = useAttributeGroupForm({
    initial,
    onSuccess,
  });

  const { mutateAsync: deleteGroup } = useDeleteAttributeGroup();

  const { control } = form;
  const { formState } = form;

  useEffect(() => {
    onUnsavedChange?.(formState.isDirty);
  }, [formState.isDirty, onUnsavedChange]);

  const handleDelete = async () => {
    if (initial && initial.code !== 'system') {
      await deleteGroup({ code: initial.code });
      resetFormGuard();
      navigate('/attribute-groups', { replace: true });
    }
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="border-b flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {isEdit ? (
          <EditableTitle
            value={form.watch('name')}
            label={t('attributeGroups:renameGroup')}
            onSave={handleTitleSave}
            disabled={isDisabled}
            placeholder={t('attributeGroups:editGroup')}
            textClassName="text-xl font-semibold"
          />
        ) : (
          <CardTitle>{t('attributeGroups:addGroup')}</CardTitle>
        )}
        {!isDisabled && (
          <SaveButton
            className="self-end sm:self-auto"
            isDirty={formState.isDirty}
            isLoading={isLoading}
            onClick={onSubmit}
            secondaryAction={
              isEdit &&
              initial?.code !== 'system' && (
                <ConfirmationButton
                  label={t('attributeGroups:deleteGroup')}
                  onConfirm={handleDelete}
                  title={t('attributeGroups:removeGroupTitle')}
                  description={<Trans>{t('attributeGroups:removeGroupDescription', { name: initial?.name })}</Trans>}
                />
              )
            }
          />
        )}
      </CardHeader>
      <CardContent className="flex-1 overflow-hidden p-0">
        <FormProvider {...form}>
          <ScrollArea className="h-full">
            <div className="flex flex-col gap-4 p-2 md:p-6 max-w-3xl">
              <div className="flex flex-col gap-4">
                {isEdit ? (
                  <div className="flex flex-col gap-1">
                    <span className="text-sm text-muted-foreground">{t('attributeGroups:code')}</span>
                    <span className="font-mono text-sm">{initial?.code}</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <AiInput
                      control={control}
                      name="name"
                      label={t('attributeGroups:name')}
                      required
                      isProcessing={isGeneratingCode}
                      disabled={isDisabled}
                      onBlur={handleNameBlur}
                    />
                    <Input
                      control={control}
                      name="code"
                      label={t('attributeGroups:code')}
                      required
                      disabled={isDisabled || isCodeLocked}
                      placeholder={isCodeLocked ? t('common:nameIsTheFirst') : undefined}
                    />
                  </div>
                )}

                <Textarea
                  control={control}
                  name="description"
                  label={t('attributeGroups:description')}
                  rows={4}
                  disabled={isDisabled}
                  classNames={{ input: 'max-h-[200px]' }}
                />
              </div>

              <AttributesList disabled={isDisabled} />
            </div>
          </ScrollArea>
        </FormProvider>
      </CardContent>
    </Card>
  );
};
